import React, { useState, useEffect, useRef } from 'react';
import { navigatorApi } from '../../api/navigator';
import './StoryNavigatorBadge.css';

/**
 * Badge showing current Navigator for a story with dropdown to change.
 * Displays icon + name, with click-to-change functionality.
 */
function StoryNavigatorBadge({ storyId, onNavigatorChange }) {
  const [config, setConfig] = useState(null);
  const [navigators, setNavigators] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Load Navigator config and available navigators
  useEffect(() => {
    if (!storyId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const [configData, navigatorsList] = await Promise.all([
          navigatorApi.get(storyId),
          navigatorApi.listAvailable('fiction')
        ]);
        setConfig(configData);
        setNavigators(navigatorsList || []);
      } catch (err) {
        console.error('Failed to load Navigator data:', err);
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [storyId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleNavigatorSelect = async (navigatorKey) => {
    try {
      const updatedConfig = await navigatorApi.set(storyId, {
        primary_key: navigatorKey,
        secondary_keys: config?.secondary_keys || [],
        story_flavor: config?.story_flavor || null
      });
      setConfig(updatedConfig);
      setIsOpen(false);
      if (onNavigatorChange) {
        onNavigatorChange(updatedConfig);
      }
    } catch (err) {
      console.error('Failed to update Navigator:', err);
    }
  };

  if (loading) {
    return (
      <div className="story-navigator-badge story-navigator-badge--loading">
        <span className="navigator-badge__loading">...</span>
      </div>
    );
  }

  if (error || !config) {
    return null; // Don't show badge if error or no config
  }

  // Find current navigator details
  // Note: API returns primary_key, not navigator_primary
  const currentNavigator = navigators.find(n => n.key === config.primary_key) || {
    key: config.primary_key || 'general_editorial',
    name: config.primary_key === 'general_editorial' ? 'General Editorial' : config.primary_key,
    icon: '📝'
  };

  return (
    <div className="story-navigator-badge" ref={dropdownRef}>
      <button
        className="navigator-badge__trigger"
        onClick={() => setIsOpen(!isOpen)}
        title="Change Navigator"
      >
        <span className="navigator-badge__icon">{currentNavigator.icon}</span>
        <span className="navigator-badge__name">{currentNavigator.name}</span>
        <span className="navigator-badge__arrow">{isOpen ? '▴' : '▾'}</span>
      </button>

      {isOpen && (
        <div className="navigator-badge__dropdown">
          <div className="navigator-badge__dropdown-header">
            Change Navigator
          </div>
          <div className="navigator-badge__dropdown-list">
            {navigators.map(nav => (
              <button
                key={nav.key}
                className={`navigator-badge__dropdown-item ${nav.key === currentNavigator.key ? 'navigator-badge__dropdown-item--active' : ''}`}
                onClick={() => handleNavigatorSelect(nav.key)}
              >
                <span className="dropdown-item__icon">{nav.icon}</span>
                <span className="dropdown-item__name">{nav.name}</span>
                {nav.key === currentNavigator.key && (
                  <span className="dropdown-item__check">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default StoryNavigatorBadge;
